import { I18n } from "@assets/strings";
import React, { useState } from "react";
import { ClipLoader } from "react-spinners";
import styled from "styled-components";
import {
	AfricanCountries,
	AfricanCountry,
	AfricanCountryCode,
	AfricancountryCodes,
	allAfricanCountry,
} from "@domain/application/africanCountryCode";
import {
	allApplicationMemberStatus,
	ApplicationFilterOptions,
	ApplicationLevel,
	ApplicationLevels,
	ApplicationMemberStatus,
} from "@domain/application/application";
import { useApplicationsLists } from "@domain/application/hooks";
import { notEmpty } from "@utils/collection";
import { SecondaryTouchButton } from "@ui/components/buttons/primaryButton";
import { Primary, PrimaryMedium } from "@ui/components/containers/primary";
import { InfiniteScroll } from "@ui/components/infiniteScroll";
import { Selecter } from "@ui/components/inputs/selecter";
import { Row, Stack } from "@ui/components/layout";
import { ApplicationView } from "./applicationView";

const columns = ["fullname", "residence", "degree", "program", "status"] as const;

export const ApplicationTabs: React.FC = () => {
	const [filterOption, setFilterOption] = useState<ApplicationFilterOptions | undefined>(undefined);
	const { result: applications, loading, fetchMore, totalElements } = useApplicationsLists(filterOption);

	return !applications ? (
		loading ? (
			<ClipLoader color="#000" />
		) : null
	) : (
		<Stack gap={20}>
			<Row gap={20} align="center">
				<Selecter
					id="countries"
					value={AfricanCountries[filterOption?.countryResidence as AfricanCountryCode]}
					placeholder={I18n.t("backoffice.application.residence")}
					onChange={(v: string) =>
						setFilterOption({ ...filterOption, countryResidence: AfricancountryCodes[v as AfricanCountry] })
					}
					options={allAfricanCountry.map((country) => ({ label: country, value: country }))}
				/>
				<Selecter
					id="degrees"
					value={filterOption?.levelName}
					placeholder={I18n.t("backoffice.application.degree")}
					onChange={(v: string) => setFilterOption({ ...filterOption, levelName: v as ApplicationLevel })}
					options={ApplicationLevels.map((level) => ({ label: level, value: level }))}
				/>
				<Selecter
					id="status"
					value={filterOption?.status}
					placeholder={I18n.t("backoffice.application.status")}
					onChange={(v: string) => setFilterOption({ ...filterOption, status: v as ApplicationMemberStatus })}
					options={allApplicationMemberStatus
						.filter((status) => status !== "COMPLETED")
						.map((status) => ({ label: I18n.t(`application.status.${status}`), value: status }))}
				/>
				<SecondaryTouchButton
					disabled={!filterOption}
					onClick={() => {
						if (!filterOption) return;
						setFilterOption(undefined);
					}}
				>
					{I18n.t("backoffice.application.filter.reset")}
				</SecondaryTouchButton>
			</Row>
			<Container>
				<TabHeader>
					{columns.map((column) => (
						<PrimaryMedium key={column}>{I18n.t(`backoffice.application.${column}`)}</PrimaryMedium>
					))}
				</TabHeader>
				{loading ? (
					<ClipLoader />
				) : (
					<Table>
						<InfiniteScroll loading={loading} onEndReached={fetchMore}>
							{applications.filter(notEmpty).map((application) => (
								<ApplicationView key={application.id} application={application} />
							))}
						</InfiniteScroll>
					</Table>
				)}
			</Container>
			<Row gap={5}>
				<StyledPrimary>{I18n.t("backoffice.application.filter.candidat")} : </StyledPrimary>
				<Primary>{totalElements}</Primary>
			</Row>
		</Stack>
	);
};

const Container = styled.div`
	display: flex;
	flex-direction: column;
	border: 1px solid rgba(114, 188, 243, 0.8);
	border-radius: 7px;
	width: 100%;
`;
const TabHeader = styled.div`
	display: grid;
	grid-template-columns: repeat(5, 1fr);
	grid-template-rows: auto;
	background: rgba(114, 188, 243, 0.08);
	place-items: center;
	padding: 20px 0px;
	border-bottom: 1px solid rgba(114, 188, 243, 0.8);
`;

const Table = styled.div`
	overflow-y: scroll;
	height: 400px;
`;

const StyledPrimary = styled(PrimaryMedium)`
	color: ${({ theme }) => theme.colors.secondary};
`;
